import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';

const SalesContractManagementReport = () => {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [search,  setSearch]  = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [totals, setTotals] = useState({
    totalContractQty:  0,
    totalCancelledQty: 0,
    totalNetQty:       0,
    totalDoIssuedQty:  0,
    totalBalance:      0,
  });

  const [filters, setFilters] = useState({
    contractNo:          '',
    buyerName:           '',
    commodity:           '',
    balanceMoreThanZero: false,
    utilizationPct:      '',
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [showFilters, setShowFilters] = useState(false);

  const navigate      = useNavigate();
  const ROWS_PER_PAGE = 25;

  // ── Debounce search ──────────────────────────────────────────────────────────
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(h);
  }, [search]);

  // ── Debounce filters ─────────────────────────────────────────────────────────
  useEffect(() => {
    const h = setTimeout(() => setDebouncedFilters(filters), 400);
    return () => clearTimeout(h);
  }, [filters]);

  // ── Formatters ───────────────────────────────────────────────────────────────
  const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '-';
    return parseFloat(num).toLocaleString('en-IN', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  };

  const formatRate = (num) => {
    if (num === null || num === undefined || num === '') return '-';
    return parseFloat(num).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  // ── Fetch data ───────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page:  page.toString(),
        limit: ROWS_PER_PAGE.toString(),
        ...(debouncedSearch                        && { search:              debouncedSearch                        }),
        ...(debouncedFilters.contractNo            && { contractNo:          debouncedFilters.contractNo            }),
        ...(debouncedFilters.buyerName             && { buyerName:           debouncedFilters.buyerName             }),
        ...(debouncedFilters.commodity             && { commodity:           debouncedFilters.commodity             }),
        ...(debouncedFilters.balanceMoreThanZero   && { balanceMoreThanZero: 'true'                                 }),
        ...(debouncedFilters.utilizationPct !== '' && { utilizationPct:      debouncedFilters.utilizationPct        }),
      });

      const response = await fetch(
        `${API_BASE_URL}/api/sales/sales-contract-management-report?${params}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
        setTotal(result.total || 0);
        setTotals({
          totalContractQty:  result.totalContractQty  || 0,
          totalCancelledQty: result.totalCancelledQty || 0,
          totalNetQty:       result.totalNetQty       || 0,
          totalDoIssuedQty:  result.totalDoIssuedQty  || 0,
          totalBalance:      result.totalBalance      || 0,
        });
      } else {
        setData([]); setTotal(0);
        setTotals({ totalContractQty: 0, totalCancelledQty: 0, totalNetQty: 0, totalDoIssuedQty: 0, totalBalance: 0 });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]); setTotal(0);
      setTotals({ totalContractQty: 0, totalCancelledQty: 0, totalNetQty: 0, totalDoIssuedQty: 0, totalBalance: 0 });
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, debouncedFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(1); };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({ contractNo: '', buyerName: '', commodity: '', balanceMoreThanZero: false, utilizationPct: '' });
    setSearch('');
    setPage(1);
  };

  const handleViewDetails = (contract) =>
    navigate(`/sale-contract/view/${contract.sales_contract_id}`);

  // ── Pagination ───────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const isLastPage = page === totalPages;
  const handlePrev = () => setPage(p => Math.max(1, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages, p + 1));

  // ── Utilization color helper ─────────────────────────────────────────────────
  const getUtilizationColor = (pct) => {
    if (pct >= 100) return 'text-red-600';
    if (pct >= 75)  return 'text-orange-500';
    if (pct >= 50)  return 'text-yellow-600';
    return 'text-green-600';
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">

      {/* ── Print CSS ── */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 8mm 10mm;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print,
          .print-hide-action {
            display: none !important;
          }
          table {
            width: 100% !important;
            font-size: 9px !important;
            border-collapse: collapse !important;
          }
          th, td {
            padding: 3px 4px !important;
            font-size: 9px !important;
            white-space: nowrap !important;
          }
          thead tr {
            background-color: #f3f4f6 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          tbody tr:nth-child(even) {
            background-color: #f9fafb !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          tfoot tr {
            background-color: #e5e7eb !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          tr { page-break-inside: avoid; }
          .overflow-x-auto { overflow: visible !important; }
        }
      `}</style>

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Sales Contract Management Report</h2>
        <div className="flex gap-1 w-full sm:w-auto no-print">
          <input
            type="text"
            placeholder="Quick Search..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1 px-3 py-1 text-xs rounded font-medium border ${
              showFilters
                ? 'bg-gray-600 text-white border-gray-700 hover:bg-gray-700'
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
            }`}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1 bg-green-600 text-white px-3 py-1 text-xs rounded hover:bg-green-700 border border-green-700 font-medium"
          >
            Print
          </button>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 bg-[#3899cf] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
          >
            <FaArrowLeft /> Back
          </button>
        </div>
      </div>

      {/* ── Filters Panel ── */}
      {showFilters && (
        <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200 no-print">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Contract No.</label>
              <input
                type="text"
                placeholder="Enter contract no."
                value={filters.contractNo}
                onChange={(e) => handleFilterChange('contractNo', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Buyer Name</label>
              <input
                type="text"
                placeholder="Search buyer..."
                value={filters.buyerName}
                onChange={(e) => handleFilterChange('buyerName', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Commodity</label>
              <input
                type="text"
                placeholder="Search commodity..."
                value={filters.commodity}
                onChange={(e) => handleFilterChange('commodity', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Utilization % <span className="text-gray-400 font-normal">(≤ entered value)</span>
              </label>
              <input
                type="number"
                placeholder="e.g. 50"
                min="0"
                max="100"
                value={filters.utilizationPct}
                onChange={(e) => handleFilterChange('utilizationPct', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Balance Filter</label>
              <div className="flex items-center h-8">
                <input
                  type="checkbox"
                  id="balanceMoreThanZero"
                  checked={filters.balanceMoreThanZero}
                  onChange={(e) => handleFilterChange('balanceMoreThanZero', e.target.checked)}
                  className="mr-2 h-4 w-4 text-[#3899cf] focus:ring-[#3899cf] border-gray-300 rounded"
                />
                <label htmlFor="balanceMoreThanZero" className="text-sm text-gray-700">
                  Balance &gt; 0
                </label>
              </div>
            </div>

          </div>

          {filters.utilizationPct !== '' && (
            <p className="mt-2 text-xs text-blue-600">
              Showing contracts with utilization ≤ <strong>{filters.utilizationPct}%</strong>.
              Sorted: highest to lowest utilization.
            </p>
          )}

          <div className="mt-3 flex justify-end">
            <button
              onClick={handleResetFilters}
              className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium"
            >
              Reset All Filters
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : data.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">No sales contracts found.</div>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-100">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-2 text-left  font-medium whitespace-nowrap">Sales Contract No.</th>
                <th className="px-2 py-2 text-left  font-medium whitespace-nowrap">Commodity</th>
                <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Contract Qty. (MT)</th>
                <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Cancelled Qty. (MT)</th>
                <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Net Qty. (MT)</th>
                <th className="px-2 py-2 text-right font-medium whitespace-nowrap">DO Issued Qty. (MT)</th>
                <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Balance (MT)</th>
                <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Utilization %</th>
                <th className="px-2 py-2 text-left  font-medium whitespace-nowrap">Start Date</th>
                <th className="px-2 py-2 text-left  font-medium whitespace-nowrap">End Date</th>
                <th className="px-2 py-2 text-right font-medium whitespace-nowrap">Rate PMT (₹)</th>
                <th className="px-2 py-2 text-left  font-medium whitespace-nowrap">Buyer</th>
                <th className="px-2 py-2 text-left  font-medium whitespace-nowrap">Contract Date</th>
                <th className="px-2 py-2 text-left  font-medium whitespace-nowrap print-hide-action">Actions</th>
              </tr>
            </thead>

            <tbody>
              {data.map((contract, idx) => {
                const utilPct = parseFloat(contract.utilization_pct ?? 0);
                const balance = parseFloat(contract.balance_for_contract_qty_mt || 0);
                return (
                  <tr
                    key={contract.sales_contract_id}
                    className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    <td className="px-2 py-1 font-medium whitespace-nowrap">
                      {contract.sales_contract_no}
                    </td>
                    <td className="px-2 py-1 max-w-[5rem] truncate" title={contract.commodity_name}>
                      {contract.commodity_name || '-'}
                    </td>
                    <td className="px-2 py-1 font-medium whitespace-nowrap text-right">
                      {formatNumber(contract.contract_quantity_mt)}
                    </td>
                    <td className="px-2 py-1 font-medium whitespace-nowrap text-right text-orange-600">
                      {formatNumber(contract.cancelled_qty)}
                    </td>
                    <td className="px-2 py-1 font-medium whitespace-nowrap text-right text-blue-700">
                      {formatNumber(contract.net_qty)}
                    </td>
                    <td className="px-2 py-1 font-medium whitespace-nowrap text-right">
                      {formatNumber(contract.do_issued_quantity_mt)}
                    </td>
                    <td className={`px-2 py-1 font-medium whitespace-nowrap text-right ${
                      balance <= 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatNumber(contract.balance_for_contract_qty_mt)}
                    </td>
                    <td className={`px-2 py-1 font-medium whitespace-nowrap text-right ${getUtilizationColor(utilPct)}`}>
                      {contract.utilization_pct !== null ? `${utilPct.toFixed(2)}%` : '-'}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {formatDate(contract.delivery_start_date)}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {formatDate(contract.delivery_end_date)}
                    </td>
                    <td className="px-2 py-1 font-medium whitespace-nowrap text-right">
                      {formatRate(contract.sale_price_per_mt)}
                    </td>
                    <td className="px-2 py-1 max-w-[6rem] truncate" title={contract.buyer_name}>
                      {contract.buyer_name || '-'}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {formatDate(contract.contract_date)}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap print-hide-action">
                      <button
                        onClick={() => handleViewDetails(contract)}
                        className="bg-[#3899cf] hover:bg-[#0e8dc6] text-white px-2 py-1 rounded text-xs flex items-center gap-1"
                      >
                        <FaEye /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* ── Grand Total — last page only ── */}
            {isLastPage && (
              <tfoot>
                <tr className="font-bold bg-gray-200">
                  <td colSpan="2" className="px-2 py-2 text-right text-xs">Grand Total:</td>
                  <td className="px-2 py-2 text-blue-700 text-right text-xs">
                    {formatNumber(totals.totalContractQty)}
                  </td>
                  <td className="px-2 py-2 text-orange-700 text-right text-xs">
                    {formatNumber(totals.totalCancelledQty)}
                  </td>
                  <td className="px-2 py-2 text-blue-800 text-right text-xs">
                    {formatNumber(totals.totalNetQty)}
                  </td>
                  <td className="px-2 py-2 text-blue-700 text-right text-xs">
                    {formatNumber(totals.totalDoIssuedQty)}
                  </td>
                  <td className={`px-2 py-2 text-right text-xs ${
                    parseFloat(totals.totalBalance) <= 0 ? 'text-red-700' : 'text-green-700'
                  }`}>
                    {formatNumber(totals.totalBalance)}
                  </td>
                  {/* Empty cell for utilization column */}
                  <td></td>
                  <td colSpan="6"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-between items-center mt-3 text-xs text-gray-500 no-print">
          <span>
            Showing {Math.min((page - 1) * ROWS_PER_PAGE + 1, total)}–
            {Math.min(page * ROWS_PER_PAGE, total)} of {total} records
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              disabled={page === 1}
              className={`px-2 py-1 rounded border transition-colors duration-100 ${
                page === 1
                  ? 'bg-gray-100 text-gray-300 border-gray-200'
                  : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
              }`}
            >Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button
              onClick={handleNext}
              disabled={page === totalPages}
              className={`px-2 py-1 rounded border transition-colors duration-100 ${
                page === totalPages
                  ? 'bg-gray-100 text-gray-300 border-gray-200'
                  : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
              }`}
            >Next</button>
          </div>
        </div>
      )}

    </div>
  );
};

export default SalesContractManagementReport;